// T016 — Wallet transactions table with audit-log filter (US4) and "Load more" pagination.
import { useMemo } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";

import { TRANSACTION_TYPE_META, isAdminActionType, sourceProgramLabel } from "../consts";
import { formatEur } from "../../../utils/eur";
import { formatDate } from "../../../utils/formats";
import type { WalletTransaction, WalletTransactionType } from "../../../types/wallet";
import AdminAuditFilter from "./AdminAuditFilter";
import type { AuditFilterValue } from "./AdminAuditFilter";

interface TransactionsTableProps {
  items: WalletTransaction[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  filter: AuditFilterValue;
  onFilterChange: (value: AuditFilterValue) => void;
}

// US4 — narrow rows when the audit-log filter is active. Cross-wallet navigation
// is intentionally out of scope for v1: all rows belong to the wallet currently
// open on this page, so the table is read-only and self-contained (FR-015/FR-016).
const TransactionsTable = ({
  items,
  loading,
  hasMore,
  onLoadMore,
  filter,
  onFilterChange,
}: TransactionsTableProps) => {
  const visibleItems = useMemo(() => {
    if (filter === "admin_only") {
      return items.filter((t) => isAdminActionType(t.type));
    }
    return items;
  }, [items, filter]);

  return (
    <Card>
      <div className="flex flex-column sm:flex-row align-items-start sm:align-items-center justify-content-between gap-2 mb-3">
        <h3 className="m-0">Transaction history</h3>
        <AdminAuditFilter value={filter} onChange={onFilterChange} />
      </div>

      <DataTable
        value={visibleItems}
        size="small"
        emptyMessage={
          filter === "admin_only"
            ? "No admin actions recorded for this wallet."
            : "No transactions yet."
        }
      >
        <Column
          header="Type"
          body={(row: WalletTransaction) => {
            const meta = TRANSACTION_TYPE_META[row.type as WalletTransactionType];
            if (!meta) return row.type;
            return (
              <Tag
                value={meta.label}
                severity={meta.severity}
                icon={meta.icon}
                className="text-xs"
              />
            );
          }}
        />
        <Column header="Amount" body={(row: WalletTransaction) => formatEur(row.amount)} />
        <Column
          header="Resulting balance"
          body={(row: WalletTransaction) => formatEur(row.resulting_balance)}
        />
        <Column field="actor" header="Actor" body={(row) => row.actor || "system"} />
        <Column
          header="Reference"
          body={(row: WalletTransaction) => {
            // Program and its reference together identify the grant (e.g. which
            // referral), so show every part that is present.
            const parts = [
              row.related_session_reference,
              sourceProgramLabel(row.related_source_program),
              row.related_source_reference,
            ].filter(Boolean);
            if (parts.length > 0) return parts.join(" · ");
            return row.related_batch_id ? row.related_batch_id.slice(0, 8) : "—";
          }}
        />
        <Column field="note" header="Note" body={(row) => row.note ?? ""} />
        <Column header="When" body={(row: WalletTransaction) => formatDate(row.created_at)} />
      </DataTable>

      {hasMore && (
        <div className="flex justify-content-center mt-3">
          <Button
            label="Load more"
            icon="pi pi-chevron-down"
            severity="secondary"
            text
            onClick={onLoadMore}
            loading={loading}
          />
        </div>
      )}
    </Card>
  );
};

export default TransactionsTable;
